type ByteRange = readonly [min: number, max: number];

interface Decoded {
  readonly text: string;
  readonly length: number;
}

interface Utf8Sequence {
  readonly length: number;
  readonly leadMask: number;
  readonly secondByteRange: ByteRange;
}

interface Utf8Shape {
  readonly leadBytes: ByteRange;
  readonly length: number;
  readonly leadMask: number;
}

const PERCENT = '%';
const SPACE_FOR_PLUS: Decoded = { text: ' ', length: 1 };
const ESCAPE_LENGTH = 3;
const HEX_RADIX = 16;
const ASCII_LIMIT = 0b1000_0000;
const HEX_PAIR = /^[\dA-Fa-f]{2}$/;
const CONTINUATION_BITS = 6;
const CONTINUATION_MASK = 0b0011_1111;
const CONTINUATION_RANGE: ByteRange = [0b1000_0000, 0b1011_1111];

/** Second-byte ranges that reject overlong forms (`E0`, `F0`), surrogates (`ED`) and values above U+10FFFF (`F4`). */
const SECOND_BYTE_RANGE_BY_LEAD: ReadonlyMap<number, ByteRange> = new Map<number, ByteRange>([
  [0b1110_0000, [0b1010_0000, 0b1011_1111]],
  [0b1110_1101, [0b1000_0000, 0b1001_1111]],
  [0b1111_0000, [0b1001_0000, 0b1011_1111]],
  [0b1111_0100, [0b1000_0000, 0b1000_1111]],
]);

/** Lead bytes of the well-formed sequences: `C0`, `C1` and `F5` to `FF` never start one. */
const UTF8_SHAPES: readonly Utf8Shape[] = [
  { leadBytes: [0b1100_0010, 0b1101_1111], length: 2, leadMask: 0b0001_1111 },
  { leadBytes: [0b1110_0000, 0b1110_1111], length: 3, leadMask: 0b0000_1111 },
  { leadBytes: [0b1111_0000, 0b1111_0100], length: 4, leadMask: 0b0000_0111 },
];

/**
 * Decodes query text by ASP.NET's query-parser rule: `+` becomes a space and each valid `%XX` escape is decoded,
 * multi-byte UTF-8 sequences included. A `%` not followed by two hex digits, and any escape that is not part of a
 * well-formed UTF-8 sequence, stays literal; decoding resumes at the next escape. It never throws.
 *
 * `decodeURIComponent` throws on a malformed escape and `URLSearchParams` turns invalid bytes into U+FFFD, so
 * neither fits the rule the server applies.
 *
 * @param text The raw query key or value.
 * @returns The decoded text.
 */
export function formDecode(text: string): string {
  const parts: string[] = [];
  let literalStart = 0;
  let index = 0;

  while (index < text.length) {
    const char = text[index];
    const decoded = char === '+' ? SPACE_FOR_PLUS : decodeEscapeAt(text, index);
    if (decoded === null) {
      index += 1;
      continue;
    }

    parts.push(text.slice(literalStart, index), decoded.text);
    index += decoded.length;
    literalStart = index;
  }

  parts.push(text.slice(literalStart));
  return parts.join('');
}

/**
 * Decodes the escape that starts at `index`, together with the continuation escapes of a multi-byte UTF-8 sequence.
 *
 * @param text The raw text.
 * @param index Where a `%` is expected.
 * @returns The decoded character and the raw length it covers, or `null` when the text there stays literal.
 */
function decodeEscapeAt(text: string, index: number): Decoded | null {
  const lead = escapedByteAt(text, index);
  if (lead === null) {
    return null;
  }
  if (lead < ASCII_LIMIT) {
    return { text: String.fromCodePoint(lead), length: ESCAPE_LENGTH };
  }

  const sequence = utf8SequenceOf(lead);
  if (sequence === null) {
    return null;
  }

  let codePoint = lead & sequence.leadMask;
  for (let offset = 1; offset < sequence.length; offset += 1) {
    const byte = escapedByteAt(text, index + offset * ESCAPE_LENGTH);
    const allowed = offset === 1 ? sequence.secondByteRange : CONTINUATION_RANGE;
    if (byte === null || !isWithin(byte, allowed)) {
      return null;
    }
    codePoint = (codePoint << CONTINUATION_BITS) | (byte & CONTINUATION_MASK);
  }

  return { text: String.fromCodePoint(codePoint), length: sequence.length * ESCAPE_LENGTH };
}

/**
 * Reads one `%XX` escape.
 *
 * @param text The raw text.
 * @param index Where the `%` is expected.
 * @returns The escaped byte, or `null` when no valid escape starts at `index`.
 */
function escapedByteAt(text: string, index: number): number | null {
  if (text[index] !== PERCENT || index + ESCAPE_LENGTH > text.length) {
    return null;
  }

  const hex = text.slice(index + 1, index + ESCAPE_LENGTH);
  return HEX_PAIR.test(hex) ? Number.parseInt(hex, HEX_RADIX) : null;
}

/**
 * Tells whether a byte lies in an inclusive range.
 *
 * @param byte The byte to test.
 * @param range The lowest and highest allowed byte.
 * @returns Whether the byte is in the range.
 */
function isWithin(byte: number, range: ByteRange): boolean {
  return byte >= range[0] && byte <= range[1];
}

/**
 * Maps a UTF-8 lead byte to the shape of its sequence.
 *
 * @param lead A byte of `0x80` or more.
 * @returns The sequence shape, or `null` for a stray continuation byte, `C0`, `C1` and `F5` to `FF`.
 */
function utf8SequenceOf(lead: number): Utf8Sequence | null {
  const shape = UTF8_SHAPES.find(({ leadBytes }) => isWithin(lead, leadBytes));
  if (shape === undefined) {
    return null;
  }

  return {
    length: shape.length,
    leadMask: shape.leadMask,
    secondByteRange: SECOND_BYTE_RANGE_BY_LEAD.get(lead) ?? CONTINUATION_RANGE,
  };
}
